<template>
  <div ref="wrapperRef" class="m-fileupload-wrapper">
    <FileUpload
      mode="basic"
      :multiple="multiple"
      :disabled="disabled"
      :chooseLabel="label || placeholder"
      :showUploadButton="false"
      :showCancelButton="false"
      :class="fileUploadClasses"
      @select="onFileSelect"
      @blur="handleBlur"
    />

    <!-- Selected file display -->
    <div v-if="selectedFile" class="file-info">
      <i class="pi pi-file mr-2"></i>
      <span class="file-name">{{ selectedFile.name }}</span>
      <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
      <Button
        v-if="clearable"
        icon="pi pi-times"
        class="p-button-text p-button-sm"
        @click="clearFile"
      />
    </div>

    <div v-if="hint" class="m-fileupload-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import FileUpload from 'primevue/fileupload'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface Props {
  modelValue?: File | null
  label?: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  multiple?: boolean
  clearable?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  placeholder: 'Выберите файл',
  hint: '',
  disabled: false,
  multiple: false,
  clearable: true,
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [file: File | null]
  'change': [file: File | null]
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const selectedFile = ref<File | null>(props.modelValue)

const fileUploadClasses = computed(() => [
  'm-fileupload',
  {
    'm-fileupload--disabled': props.disabled
  }
])

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const onFileSelect = (event: any) => {
  const file = event.files && event.files[0]
  if (file) {
    selectedFile.value = file
    emit('update:modelValue', file)
    emit('change', file)
  }
}

const clearFile = () => {
  selectedFile.value = null
  emit('update:modelValue', null)
  emit('change', null)
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-fileupload-wrapper {
  width: 100%;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f3f4f6;
  border-radius: 4px;
  margin-top: 0.5rem;

  .file-name {
    flex: 1;
    font-weight: 500;
    color: #374151;
  }

  .file-size {
    color: #6b7280;
    font-size: 0.875rem;
  }
}

.m-fileupload-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>

